/* eslint-disable react/no-danger */
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { t } from "i18next";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import styled from "@emotion/styled";
import { useEffect, useState } from "react";

const NavigationControls = styled(Box)(({ theme }) => ({
	position: "absolute",
	left: 0,
	right: 0,
	bottom: 0,
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
	padding: theme.spacing(0.5, 1),
	backgroundColor: "rgba(0, 0, 0, 0.4)",
	color: "#fff",
	zIndex: 10
}));

const NavButton = styled(IconButton)(({ theme }) => ({
	color: "#fff",
	backgroundColor: "rgba(0, 0, 0, 0.2)",
	"&:hover": {
		backgroundColor: "rgba(0, 0, 0, 0.4)"
	},
	"&.Mui-disabled": {
		color: "rgba(255, 255, 255, 0.3)"
	}
}));

const officeMimeTypes = [
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.ms-excel",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"application/vnd.ms-powerpoint",
	"application/vnd.openxmlformats-officedocument.presentationml.presentation"
];

const RenderAssetDisplay = ({ asset, currentLanguage }) => {
	const [currentAttachmentIndex, setCurrentAttachmentIndex] = useState(0);
	const [previewContent, setPreviewContent] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isFullscreen, setIsFullscreen] = useState(false);

	const toggleFullscreen = () => {
		setIsFullscreen(!isFullscreen);
	};

	const getCurrentLang = () => currentLanguage.toUpperCase();

	const getAttachments = () => {
		const currentLang = getCurrentLang();
		return asset?.content?.[0]?.[currentLang]?.attachments || [];
	};

	const getCurrentAttachment = () => {
		const attachments = getAttachments();
		return attachments.length > 0 ? attachments[currentAttachmentIndex] : null;
	};

	const isDocumentType =
		asset?.fileType === "documents" ||
		asset?.fileType === "images" ||
		asset?.fileType === "videos" ||
		asset?.fileType === "document" ||
		asset?.fileType === "css";
	const hasAttachment = !!getCurrentAttachment();
	const attachments = asset?.content?.[0]?.[getCurrentLang()]?.attachments || [];
	const hasMultipleAttachments = attachments.length > 1;

	const handleDownload = () => {
		const attachment = getCurrentAttachment();
		if (attachment?.url) {
			window.open(attachment.url, "_blank");
		}
	};

	const handleNextAttachment = () => {
		const attachments = getAttachments();
		if (currentAttachmentIndex < attachments.length - 1) {
			setCurrentAttachmentIndex(currentAttachmentIndex + 1);
		}
	};

	const handlePrevAttachment = () => {
		if (currentAttachmentIndex > 0) {
			setCurrentAttachmentIndex(currentAttachmentIndex - 1);
		}
	};

	useEffect(() => {
		const generatePreview = async () => {
			if (!asset || !asset.content?.[0]) {
				setPreviewContent(
					<Typography variant="body2">{t("ASSETS.DETAILS.no_content_for_language")}</Typography>
				);
				setIsLoading(false);
				return;
			}

			const currentLang = getCurrentLang();
			const attachment = getCurrentAttachment();
			const htmlTextContent = asset.content[0][currentLang]?.htmlText;

			// Réinitialiser previewContent au début pour éviter les résidus
			setPreviewContent(null);
			setIsLoading(true);
			try {
				if (asset.fileType === "htmlText") {
					if (htmlTextContent) {
						setPreviewContent(
							<div
								style={{
									maxHeight: "100%",
									width: "100%",
									padding: "0px",
									backgroundColor: "#fff"
								}}
								dangerouslySetInnerHTML={{ __html: htmlTextContent }}
							/>
						);
					} else {
						setPreviewContent(
							<Typography variant="body2">{t("ASSETS.DETAILS.no_html_content")}</Typography>
						);
					}
				} else if (
					(asset?.fileType === "documents" ||
						asset?.fileType === "images" ||
						asset?.fileType === "videos" ||
						asset?.fileType === "document" ||
						asset?.fileType === "css") &&
					attachment
				) {
					const { url, contentType, name } = attachment;

					if (contentType?.includes("image")) {
						setPreviewContent(
							<Box
								component="img"
								src={url}
								sx={{
									width: "100%",
									height: "100%",
									maxHeight: isFullscreen === true ? "90vh" : "380px",
									objectFit: "contain"
								}}
								alt={name}
							/>
						);
					} else if (contentType?.includes("video")) {
						setPreviewContent(
							<Box
								component="video"
								src={url}
								controls
								controlsList="nodownload"
								preload="metadata"
								sx={{
									width: "100%",
									height: "100%",
									maxHeight: isFullscreen === true ? "90vh" : "390px",
									maxWidth: "100%",
									objectFit: "contain",
									backgroundColor: "#000",
									borderRadius: isFullscreen === true ? 0 : 1
								}}
							>
								{t("ASSETS.DETAILS.video_not_supported")}
							</Box>
						);
					} else if (contentType?.includes("audio")) {
						setPreviewContent(
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									alignItems: "center",
									width: "100%",
									height: "100%"
								}}
							>
								<Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
									{name}
								</Typography>
								<Box
									component="audio"
									src={url}
									controls
									sx={{
										width: "100%",
										maxWidth: "500px"
									}}
								>
									{t("ASSETS.DETAILS.audio_not_supported")}
								</Box>
							</Box>
						);
					} else if (contentType?.includes("text") || contentType?.includes("css")) {
						const textResponse = await fetch(url);
						const text = await textResponse.text();
						setPreviewContent(
							<Box
								sx={{
									width: "100%",
									height: "100%",
									overflow: "auto",
									p: 2,
									backgroundColor: "grey.50"
								}}
							>
								<Typography
									variant="body2"
									sx={{
										whiteSpace: "pre-wrap",
										fontFamily: contentType?.includes("css") === true ? "monospace" : "inherit",
										fontSize: "0.875rem"
									}}
								>
									{text.slice(0, 1000)} {text.length > 1000 ? "..." : ""}
								</Typography>
							</Box>
						);
					} else if (officeMimeTypes.includes(contentType)) {
						// office
						setPreviewContent(
							<Box
								component="iframe"
								src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`}
								title="Office Document"
								sx={{
									width: "100%",
									height: "100%",
									border: "none",
									objectFit: "contain",
									minHeight: "300px"
								}}
							/>
						);
					} else if (contentType?.includes("application")) {
						setPreviewContent(
							<Box
								component="iframe"
								src={url}
								title={name}
								sx={{
									width: "100%",
									height: "100%",
									border: "none",
									objectFit: "contain",
									minHeight: "300px"
								}}
							/>
						);
					} else {
						setPreviewContent(
							<Box
								component="iframe"
								src={url}
								title={name}
								sx={{
									width: "100%",
									height: "100%",
									border: "none",
									objectFit: "contain"
								}}
							/>
						);
					}
				} else if (asset.fileType === "WebPage" || asset.fileType === "sitemap") {
					const linkUrl = asset.content[0][currentLang]?.linkUrl;
					setPreviewContent(
						<Box
							component="iframe"
							src={linkUrl}
							title={linkUrl || t("ASSETS.DETAILS.no_url")}
							sx={{ width: "100%", height: "100%", border: "none", objectFit: "contain" }}
						/>
					);
				} else {
					// Cas par défaut si aucun type ne correspond
					setPreviewContent(
						<Typography variant="body2">{t("ASSETS.DETAILS.no_preview_available")}</Typography>
					);
				}
			} catch (error) {
				setPreviewContent(
					<Typography variant="body2">{t("ASSETS.DETAILS.preview_error")}</Typography>
				);
			} finally {
				setIsLoading(false);
			}
		};

		generatePreview();
	}, [asset, t, currentLanguage, currentAttachmentIndex]);

	useEffect(() => {
		setCurrentAttachmentIndex(0);
	}, [currentLanguage]);

	const getPreviewHeight = () => {
		if (isFullscreen === true) return "100vh";

		const currentAttachment = getCurrentAttachment();
		const contentType = currentAttachment?.contentType;

		if (contentType?.includes("video") === true) return "35vh";
		if (contentType?.includes("image") === true) return "30vh";
		if (asset?.fileType === "htmlText") return "30vh";
		if (asset?.fileType === "WebPage" || asset?.fileType === "sitemap") return "40vh";

		return "30vh";
	};

	return (
		<Box
			sx={{
				position: "relative",
				mb: 3,
				padding: 0,
				width: "100%",
				height: getPreviewHeight(),
				border: isFullscreen === true ? "none" : "1px solid",
				borderColor: "grey.300",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				borderRadius: isFullscreen === true ? 0 : 2,
				backgroundColor:
					asset?.fileType === "videos" ||
					getCurrentAttachment()?.contentType?.includes("video") === true
						? "#000"
						: "white",
				color: "text.secondary",
				overflow: asset?.fileType === "htmlText" ? "auto" : "hidden",
				...(isFullscreen === true && {
					position: "fixed",
					top: 0,
					left: 0,
					zIndex: 9999,
					width: "100vw",
					backgroundColor:
						getCurrentAttachment()?.contentType?.includes("video") === true ? "#000" : "#fff",
					overflow: "auto"
				})
			}}
		>
			{!isLoading && (
				<Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 1, zIndex: 100 }}>
					{/* Bouton Fullscreen */}
					<Tooltip
						title={
							isFullscreen ? t("ASSETS.DETAILS.fullscreenExit") : t("ASSETS.DETAILS.fullscreen")
						}
					>
						<IconButton
							onClick={toggleFullscreen}
							size="small"
							sx={{
								background:
									getCurrentAttachment()?.contentType?.includes("video") === true
										? "rgba(0, 0, 0, 0.6)"
										: "rgba(255, 255, 255, 0.8)",
								color:
									getCurrentAttachment()?.contentType?.includes("video") === true
										? "#fff"
										: "inherit",
								"&:hover": {
									background:
										getCurrentAttachment()?.contentType?.includes("video") === true
											? "rgba(0, 0, 0, 0.8)"
											: "#fff"
								}
							}}
						>
							{isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
						</IconButton>
					</Tooltip>

					{/* Bouton Download (uniquement pour type document avec attachment) */}
					{isDocumentType === true && hasAttachment === true && (
						<Tooltip title={t("ASSETS.DETAILS.download")}>
							<IconButton
								onClick={handleDownload}
								size="small"
								sx={{
									background:
										getCurrentAttachment()?.contentType?.includes("video") === true
											? "rgba(0, 0, 0, 0.6)"
											: "rgba(255, 255, 255, 0.8)",
									color:
										getCurrentAttachment()?.contentType?.includes("video") === true
											? "#fff"
											: "inherit",
									"&:hover": {
										background:
											getCurrentAttachment()?.contentType?.includes("video") === true
												? "rgba(0, 0, 0, 0.8)"
												: "#fff"
									}
								}}
							>
								<DownloadIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
				</Box>
			)}

			{isLoading ? (
				<CircularProgress size={40} />
			) : (
				previewContent || (
					<Typography variant="body2" color="text.secondary">
						{t("ASSETS.DETAILS.preview")}
					</Typography>
				)
			)}
			<Box>
				{hasMultipleAttachments && !isLoading && (
					<NavigationControls>
						<NavButton
							onClick={handlePrevAttachment}
							disabled={currentAttachmentIndex === 0}
							size="small"
						>
							<ArrowBackIcon fontSize="small" />
						</NavButton>

						<Typography variant="body2" sx={{ color: "#fff", fontWeight: "medium" }}>
							{currentAttachmentIndex + 1}/{attachments.length}
						</Typography>

						<NavButton
							onClick={handleNextAttachment}
							disabled={currentAttachmentIndex === attachments.length - 1}
							size="small"
						>
							<ArrowForwardIcon fontSize="small" />
						</NavButton>
					</NavigationControls>
				)}
			</Box>
		</Box>
	);
};

export default RenderAssetDisplay;
